import React, { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import SubHeader from '../../layout/components/SubHeader';
import patientService from '../../../services/patient.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import PatientSearchForm2 from './PatientSearchForm2';
import PatientFilesToolbar from './PatientFilesToolbar';
import PatientListSkeleton from './subparts/PatientListSkeleton';
import { useNavigate } from 'react-router-dom';
import {
    DEFAULT_PATIENT_PAGE_SIZE,
    DEFAULT_PATIENT_SEARCH_COLUMN,
    DEFAULT_PATIENT_SORT,
    buildPatientsQuery,
    formatPatientCount,
} from './subparts/patientFilesQuery';
import './PatientFiles.css';
import PatientFilesIcon from './subparts/PatientFilesIcon';
import { PATIENT_FILES_PAGE_THEME } from './subparts/patientFilesListTheme';

const PatientList = lazy(() => import('./PatientList'));

const PatientManagement = () => {
    const navigate = useNavigate();
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const { patientSearch, setPatientSearch, clearPatientSearch } = useEyeconxStore();

    const {
        patients,
        searchTerm,
        searchColumn,
        sortBy,
        sortOrder,
        pageSize,
        pagination,
        pageNo,
        searchPerformed,
        showAll,
    } = patientSearch;

    const [isLoading, setIsLoading] = useState(false);

    const loadPatients = useCallback(
        async ({
            nextSearchColumn = searchColumn || DEFAULT_PATIENT_SEARCH_COLUMN,
            nextSearchTerm = searchTerm,
            nextSortBy = sortBy || DEFAULT_PATIENT_SORT.sortBy,
            nextSortOrder = sortOrder || DEFAULT_PATIENT_SORT.sortOrder,
            nextPageSize = pageSize || DEFAULT_PATIENT_PAGE_SIZE,
            nextPageNo = 0,
            nextShowAll = showAll,
        } = {}) => {
            if (!clinicId) {
                return;
            }

            setIsLoading(true);

            try {
                const query = buildPatientsQuery(
                    {
                        searchColumn: nextSearchColumn,
                        searchTerm: nextSearchTerm,
                        showAll: nextShowAll,
                    },
                    {
                        sortBy: nextSortBy,
                        sortOrder: nextSortOrder,
                    }
                );

                const result = await patientService.getAllPatients(
                    clinicId,
                    nextPageNo,
                    nextPageSize,
                    query
                );

                setPatientSearch({
                    patients: result.data.content || [],
                    pagination: {
                        totalPages: result.data.totalPages,
                        totalElements: result.data.totalElements,
                        size: result.data.size,
                        number: result.data.number,
                    },
                    searchTerm: nextShowAll ? '' : nextSearchTerm,
                    searchColumn: nextSearchColumn,
                    sortBy: nextSortBy,
                    sortOrder: nextSortOrder,
                    pageSize: nextPageSize,
                    pageNo: result.data.number ?? nextPageNo,
                    searchPerformed: true,
                    showAll: nextShowAll,
                });
            } catch (error) {
                console.error('Error loading patients:', error);
                clearPatientSearch();
            } finally {
                setIsLoading(false);
            }
        },
        [
            clinicId,
            searchColumn,
            searchTerm,
            sortBy,
            sortOrder,
            pageSize,
            showAll,
            clearPatientSearch,
            setPatientSearch,
        ]
    );

    const handleSearch = (column, term) => {
        loadPatients({
            nextSearchColumn: column || DEFAULT_PATIENT_SEARCH_COLUMN,
            nextSearchTerm: term,
            nextPageNo: 0,
            nextShowAll: false,
        });
    };

    const handlePageChange = (newPage) => {
        loadPatients({ nextPageNo: newPage });
    };

    const handleShowAllPatients = () => {
        loadPatients({
            nextSearchTerm: '',
            nextSearchColumn: DEFAULT_PATIENT_SEARCH_COLUMN,
            nextPageNo: 0,
            nextShowAll: true,
        });
    };

    const handleClearSearch = () => {
        clearPatientSearch();
    };

    const handleSortByChange = (value) => {
        loadPatients({
            nextSortBy: value,
            nextPageNo: 0,
        });
    };

    const handleSortOrderChange = (value) => {
        loadPatients({
            nextSortOrder: value,
            nextPageNo: 0,
        });
    };

    const handlePageSizeChange = (value) => {
        loadPatients({
            nextPageSize: value,
            nextPageNo: 0,
        });
    };

    const handleColumnSort = (column) => {
        const nextSortOrder =
            sortBy === column && sortOrder?.toUpperCase() === 'ASC' ? 'DESC' : 'ASC';

        loadPatients({
            nextSortBy: column,
            nextSortOrder,
            nextPageNo: 0,
        });
    };

    const handlePatientSelect = (patient) => {
        navigate(`${patient.id}`);
    };

    const description = useMemo(() => {
        if (!searchPerformed) {
            return 'Search, sort, and browse active patient records';
        }

        const total = pagination?.totalElements ?? 0;
        return `${formatPatientCount(total)} active patient record${total === 1 ? '' : 's'} loaded`;
    }, [pagination?.totalElements, searchPerformed]);

    return (
        <div className="ecx-page patient-files-page ecx-page--full-width">
            <SubHeader
                title="Patient Files"
                description={description}
                icon="bi-folder2-open"
                iconTheme={PATIENT_FILES_PAGE_THEME}
                containerClass="w-100 px-4"
            />

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel patient-files-panel">
                    <PatientSearchForm2
                        onSearch={handleSearch}
                        onShowAll={handleShowAllPatients}
                        isLoading={isLoading}
                        searchTerm={searchTerm}
                        searchColumn={searchColumn || DEFAULT_PATIENT_SEARCH_COLUMN}
                        sortBy={sortBy || DEFAULT_PATIENT_SORT.sortBy}
                        sortOrder={sortOrder || DEFAULT_PATIENT_SORT.sortOrder}
                        pageSize={pageSize || DEFAULT_PATIENT_PAGE_SIZE}
                        onSortByChange={handleSortByChange}
                        onSortOrderChange={handleSortOrderChange}
                        onPageSizeChange={handlePageSizeChange}
                    />

                    {searchPerformed && (
                        <div className="ecx-page-panel__header border-top-0 patient-files-toolbar-wrap">
                            <PatientFilesToolbar
                                isLoading={isLoading}
                                searchTerm={searchTerm}
                                searchColumn={searchColumn}
                                showAll={showAll}
                                onClearSearch={handleClearSearch}
                            />
                        </div>
                    )}

                    <div className="ecx-page-panel__body ecx-page-scroll">
                        {!searchPerformed ? (
                            <div className="patient-files-empty">
                                <PatientFilesIcon
                                    icon="bi-person-lines-fill"
                                    theme={PATIENT_FILES_PAGE_THEME}
                                    size="xl"
                                    className="patient-files-empty__icon mb-3"
                                />
                                <h5 className="patient-files-empty__title">Ready to Find Patients</h5>
                                <p className="patient-files-empty__text">
                                    Search by first name, last name, or email using the filters above,
                                    or browse all active patients in your clinic.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-light-primary"
                                    onClick={handleShowAllPatients}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-people me-1" />
                                            Show All Patients
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : isLoading ? (
                            <PatientListSkeleton count={pageSize > 8 ? 8 : pageSize} />
                        ) : (
                            <Suspense fallback={<PatientListSkeleton count={pageSize > 8 ? 8 : pageSize} />}>
                                <PatientList
                                    patients={patients}
                                    pagination={pagination}
                                    currentPage={pageNo}
                                    onPageChange={handlePageChange}
                                    onPatientSelect={handlePatientSelect}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    onColumnSort={handleColumnSort}
                                />
                            </Suspense>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientManagement;
